import { Injectable, OnDestroy } from "@angular/core";
import {ProjectCustomStatuses, ProjectSection} from "../../../../../_models/project";
import {Floor, FloorDuplicateMenu} from "../../../../../_models/floor";
import {ComponentStore} from "@ngrx/component-store";
import {finalize, Observable, of, Subject, Subscription, switchMap, takeUntil, tap} from "rxjs";
import {ProjectsService} from "../../../../../_services/projects.service";
import {ProjectAreaStatusesService} from "../../../../../_services/project-area-statuses.service";
import {FloorsService} from "../../../../../_services/floors.service";
import {Unit} from "../../../../../_models/unit";
import {Area} from "../../../../../_models/area";
import {Room} from "../../../../../_models/room";
import {TemplatesService} from "../../../../../_services/templates.service";
import {GlobalStore} from "../../../../../global.store";
import {SelectedArea, SelectedRoom} from "../types";
import {moveItemInArrayCopyAndUpdateVersion} from "../../../../../_util/utils";
import {AreaStatusSettings} from "../../../../../_models/areaStatusSettings";
import {PhaseGroup} from "../../../../../_models/phase";
import {PhaseGroupService} from "../../../../../_services/phase-group.service";
import {SectionsService} from '../../../../../_services/sections.service';
import {TableViewStateService} from '../../../../../_services/table-view-state.service';
import {NotificationService} from '../../../../../_services/notification.service';


export enum PopupOpened {
  NONE, ROOM_EDIT, ROOM_CREATE, CREATE_BASED_ON_TEMPLATE, EDIT_BASED_ON_TEMPLATE, TEMPLATE_EDIT, AREA_VIEW
}
export interface PopupState {
  popup: PopupOpened;
  selected: SelectedRoom | null;
}
export interface ProjectViewState {
  sections: ProjectSection[];
  currentSection: ProjectSection | null;
  floorsShortList: Floor[] | null;
  floorDuplicateMenuList: FloorDuplicateMenu[] | null;
  roomTemplatesShortList: Room[] | null;
  phaseGroupList: PhaseGroup[] | null;
  currentFloor: Floor | null;
  floorInLoading: number | null;
  areaStatusSettings: AreaStatusSettings | null;
  statistics: Map<number, number> | null;
  isCdkDragDisabled: boolean | null;
  popupOpened: PopupOpened | null;
  selectedObject: any | null;
}

interface LoadFloorsShortListPayload {
  goToFirst: boolean;
  sectionId?: number | null;
}

interface LoadFloorPayload {
  floorId: number;
  sectionId?: number | null;
}

@Injectable()
export class ProjectViewStore extends ComponentStore<ProjectViewState> implements OnDestroy {
  private isNotDesktop: boolean;

  companyId: number;
  projectId: number;
  private currentUserId: number | null = null;

  private readonly destroySubj$ = new Subject<void>()

  constructor(private roomTemplatesService: TemplatesService,
              private floorsService: FloorsService,
              private projectService: ProjectsService,
              readonly globalStore: GlobalStore,
              private projectAreaStatusesService: ProjectAreaStatusesService,
              private phaseGroupService: PhaseGroupService,
              private sectionsService: SectionsService,
              private tableViewStateService: TableViewStateService,
              private notificationService: NotificationService) {
    super({
      sections: [],
      currentSection: null,
      floorsShortList: null,
      floorDuplicateMenuList: null,
      roomTemplatesShortList: null,
      phaseGroupList: null,
      floorInLoading: null,
      currentFloor: null,
      areaStatusSettings: null,
      statistics: null,
      isCdkDragDisabled: false,
      popupOpened: PopupOpened.NONE,
      selectedObject: null
    });
    this.globalStore.isNotDesktop$.pipe(takeUntil(this.destroySubj$)).subscribe((isNotDesktop) => this.isNotDesktop = isNotDesktop);
    this.globalStore.companyId$.pipe(takeUntil(this.destroySubj$)).subscribe((companyId) => this.companyId = companyId);
    this.globalStore.projectId$.pipe(takeUntil(this.destroySubj$)).subscribe(projectId => this.projectId = projectId);
    this.globalStore.currentUser$.pipe(takeUntil(this.destroySubj$)).subscribe(user => this.currentUserId = user?.id ?? null);
  }

  getCurrentSelectedObject(): SelectedRoom | null {
    return this.get().selectedObject;
  }

  getCurrentSectionId(): number {
    const sectionId = this.get().currentSection?.id;
    if (!sectionId) throw new Error('No active section is selected');
    return sectionId;
  }

  readonly currentSection$ = this.select(state => state.currentSection);
  readonly sections$ = this.select(state => state.sections);

  readonly selectSection = this.updater((state: ProjectViewState, section: ProjectSection) => {
    if (!section || state.currentSection?.id === section.id) return state;

    if (this.currentUserId && this.projectId) {
      this.tableViewStateService.setActiveSectionId(this.currentUserId, this.projectId, section.id);
    }

    return {
      ...state,
      currentSection: section,
      floorsShortList: [],
      floorDuplicateMenuList: [],
      roomTemplatesShortList: [],
      currentFloor: null,
      floorInLoading: null,
      statistics: new Map()
    };
  });

  // `routeSectionId` is the `:sectionId` URL param - the authoritative source
  // now that Builder is section-scoped. Falls back to the shared last-active
  // section (kept in sync for Phases/Trades-redirect and cross-tab defaulting)
  // only when the route itself doesn't carry one.
  readonly loadSections = this.effect((trigger$: Observable<number | null | undefined>) => trigger$.pipe(
    switchMap((routeSectionId) => this.sectionsService.getSections(this.projectId).pipe(
      tap(sections => {
        const activeSections = sections.filter(section => !section.archived);
        const sharedSectionId = typeof routeSectionId === 'number'
          ? routeSectionId
          : (this.currentUserId && this.projectId
            ? this.tableViewStateService.getActiveSectionId(this.currentUserId, this.projectId)
            : undefined);
        const currentSection = (typeof sharedSectionId === 'number'
          ? activeSections.find(section => section.id === sharedSectionId)
          : null) ?? activeSections[0] ?? null;
        this.patchState({sections: activeSections, currentSection});
        if (currentSection) {
          if (typeof routeSectionId === 'number' && currentSection.id !== routeSectionId) {
            this.notificationService.successNotification(
              `Switched to ${currentSection.name} — the requested section is no longer available.`
            );
          } else if (routeSectionId === undefined && sharedSectionId === null) {
            this.notificationService.successNotification(
              `Switched to ${currentSection.name} — this module requires a single active section.`
            );
          }
          if (this.currentUserId && this.projectId) {
            this.tableViewStateService.setActiveSectionId(this.currentUserId, this.projectId, currentSection.id);
          }
          this.loadFloorsShortList(of({goToFirst: true, sectionId: currentSection.id}));
          this.loadFloorDuplicateMenuList();
          this.loadRoomTemplatesShortList();
        }
      })
    ))
  ));

  readonly floorsInLoading$ = this.select(
    (state) => state.floorInLoading ?? null,
  );

  readonly floorsShortList$ = this.select(
    (state) => state.floorsShortList ?? [],
  );

  readonly floorDuplicateMenuList$ = this.select(
    (state) => state.floorDuplicateMenuList ?? [],
  );

  readonly roomTemplatesShortList$ = this.select(
    (state) => state.roomTemplatesShortList ?? [],
  );

  readonly phaseGroupList$ = this.select(
    (state) => state.phaseGroupList ?? []
  );

  readonly floorDuplicateMenuListAsUnits$ = this.select(
    (state) => {
      const result: Unit[] = [];
      state.floorDuplicateMenuList?.forEach(floor => Array.prototype.push.apply(result, floor.units));
      return result;
    },
  );

  readonly currentFloor$ = this.select(
    (state) => state.currentFloor ?? null,
  );

  readonly statistics$ = this.select(
    (state) => state.statistics ?? new Map(),
  );

  readonly areaStatusSettings$ = this.select(
    (state) => state.areaStatusSettings ?? null,
  );

  readonly isCdkDragDisabled$ = this.select(
    (state) => state.isCdkDragDisabled,
  );

  readonly popupOpened$ = this.select(
    (state) => state.popupOpened,
  );

  readonly selectedObject$ = this.select(
    (state) => state.selectedObject,
  );

  readonly loadProjectStatuses = this.effect((trigger$: Observable<void>) => trigger$.pipe(
    switchMap(() => this.projectAreaStatusesService.getProjectAreaStatuses(this.projectId).pipe(
      tap((areaStatusSettings) => {
        const currentState = this.get();

        const statistics: Map<number, number> = new Map<number, number>();

        if (currentState.currentFloor) {
          areaStatusSettings.areaStatuses.forEach(status => statistics.set(status.id, 0));

          currentState.currentFloor.units.forEach(unit => {
            unit.rooms.forEach(room => {
              room.areas.forEach(area => statistics.set(area.status.id, statistics.get(area.status.id) + 1))
            })
          })
        }

        this.patchState({areaStatusSettings: areaStatusSettings, statistics: statistics});
      })
    ))
  ));

  readonly loadFloorsShortList = this.effect((trigger$: Observable<LoadFloorsShortListPayload>) => trigger$.pipe(
    switchMap(({goToFirst, sectionId}) => {
      const targetSectionId = sectionId ?? this.getCurrentSectionId();

      return this.floorsService.getFloorsShortList(this.projectId, targetSectionId).pipe(
      tap((floorsShortList) => {
        if (this.get().currentSection?.id !== targetSectionId) return;

        const scopedFloorsShortList = floorsShortList.filter(floor =>
          this.isFloorInSection(floor, targetSectionId, this.get().currentSection?.name)
        );

        this.patchState({floorsShortList: scopedFloorsShortList});
        if (goToFirst && scopedFloorsShortList.length) {
          this.loadFloor(of({floorId: scopedFloorsShortList[0].id, sectionId: targetSectionId}));
        } else if (!scopedFloorsShortList.length) {
          this.patchState({currentFloor: null, floorInLoading: null});
        }
      })
      )
    })
  ));

  readonly loadRoomTemplatesShortList = this.effect((trigger$: Observable<void>) => trigger$.pipe(
    switchMap(() => this.roomTemplatesService.getRoomTemplatesShortList(this.getCurrentSectionId()).pipe(
      tap((roomTemplatesShortList) => {
        this.patchState({roomTemplatesShortList: roomTemplatesShortList});
      })
    ))
  ));

  readonly loadPhaseGroups = this.effect((trigger$: Observable<void>) => trigger$.pipe(
    switchMap(() => this.phaseGroupService.getAllPhaseGroupBySection(this.getCurrentSectionId()).pipe(
      tap((phaseGroupList) => {
        this.patchState({phaseGroupList: phaseGroupList});
      })
    ))
  ))

  readonly loadFloorDuplicateMenuList = this.effect((trigger$: Observable<void>) => trigger$.pipe(
    switchMap(() => this.projectService.getUnitsListInProject(this.projectId, this.getCurrentSectionId()).pipe(
      tap((floorDuplicateMenuList) => {
        this.patchState({floorDuplicateMenuList: floorDuplicateMenuList});
      })
    ))
  ));

  readonly loadFloor = this.effect((trigger$: Observable<LoadFloorPayload>) => trigger$.pipe(
    switchMap(({floorId, sectionId}) => {
      this.patchState({floorInLoading: floorId});
      const targetSectionId = sectionId ?? this.getCurrentSectionId();

      return this.floorsService.getFloorById(floorId, targetSectionId).pipe(
        tap((floor) => {
          this.patchLoadedFloor(floor, targetSectionId);
        }),
        finalize(() => this.patchState({floorInLoading: null}))
      )
    })
  ));

  readonly updateCurrentFloor = this.updater((state: ProjectViewState, floor: Floor) => {
    return {
      ...state,
      currentFloor: structuredClone(floor)
    };
  });

  readonly updateCurrentFloorNameAndVersion = this.updater((state: ProjectViewState, floor: Floor) => {
    const updatedFloorDuplicateMenuList = state.floorDuplicateMenuList.map(item => {
      if (item.name !== state.currentFloor.name) return item;
      item.name = floor.name;
      return item;
    });

    return {
      ...state,
      currentFloor: {
        ...state.currentFloor,
        version: floor.version,
        name: floor.name
      },
      floorDuplicateMenuList: updatedFloorDuplicateMenuList
    };
  });


  readonly reloadCurrentFloor = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      tap(() => {
        const state = this.get();
        if (state.currentFloor?.id) {
          this.loadFloor(of({floorId: state.currentFloor.id, sectionId: state.currentSection?.id}));
        }
      })
    )
  );

  readonly updateSelectedObject = this.updater((state: ProjectViewState, selectedObjectInfo: {
    obj: any,
    isSelectedArea?: boolean
  }) => {
    let selectedAreaObject
    if (selectedObjectInfo.isSelectedArea) {
      selectedAreaObject = new SelectedArea();
      selectedAreaObject.area = selectedObjectInfo.obj.area;
      selectedAreaObject.sectionId = selectedObjectInfo.obj.sectionId ?? state.currentSection?.id ?? null;
      selectedAreaObject.sectionName = selectedObjectInfo.obj.sectionName ?? state.currentSection?.name ?? null;
      selectedAreaObject.floorId = selectedObjectInfo.obj.floorId;
      selectedAreaObject.floorName = selectedObjectInfo.obj.floorName;
      selectedAreaObject.unitId = selectedObjectInfo.obj.unitId;
      selectedAreaObject.unitName = selectedObjectInfo.obj.unitName;
      selectedAreaObject.roomId = selectedObjectInfo.obj.roomId;
      selectedAreaObject.roomName = selectedObjectInfo.obj.roomName;
    }
    return {
      ...state,
      selectedObject: selectedObjectInfo.isSelectedArea ? structuredClone(selectedAreaObject) : structuredClone(selectedObjectInfo.obj),
    };
  });


  readonly addRoom = this.updater((state: ProjectViewState, roomForAdd: SelectedRoom) => {
    const currentState = this.get();
    const statistics: Map<number, number> = new Map<number, number>();


    const updatedUnits = state.currentFloor.units.map(unit => {
      if (unit.id === roomForAdd.unitId) {
        unit.rooms.push(roomForAdd.room)
        return {...unit, rooms: [...unit.rooms]}
      }
      return unit
    })

    //update statuses statistics
    if (currentState.areaStatusSettings) {
      currentState.areaStatusSettings.areaStatuses.forEach(status => statistics.set(status.id, 0));
      updatedUnits.map(unit => {
        unit.rooms.map(room => {
          room.areas.forEach(area => statistics.set(area.status.id, statistics.get(area.status.id) + 1))
        })
      })
    }

    return {
      ...state,
      currentFloor: {
        ...state.currentFloor,
        units: updatedUnits
      },
      statistics: statistics
    };
  });

  readonly deleteRoom = this.updater((state: ProjectViewState, payload: { unitId: number, roomId: number }) => {
    let updatedUnits = state.currentFloor.units.map(unit => {
      if (unit.id === payload.unitId) {
        let ind = unit.rooms.findIndex(room => room.id === payload.roomId);

        const updatedRooms = [...unit.rooms].filter(room => room.id !== payload.roomId);
        updatedRooms.forEach(room => {
          if (updatedRooms.indexOf(room) >= ind) {
            room.version+=1;
          }
        });

        return {
          ...unit,
          rooms: updatedRooms
        }
      }
      return unit
    })

    return {
      ...state,
      currentFloor: {
        ...state.currentFloor,
        units: updatedUnits
      }
    };
  });

  readonly updateRoom = this.updater((state: ProjectViewState, roomForUpdate: SelectedRoom) => {
    const currentState = this.get();
    const statistics: Map<number, number> = new Map<number, number>();

    let updatedUnits = state.currentFloor.units.map(unit => {
      if (unit.id === roomForUpdate.unitId) {
        return {
          ...unit,
          rooms: unit.rooms.map(room => {
            if (room.id === roomForUpdate.room.id) {
              return roomForUpdate.room
            }
            return room
          })
        }
      }
      return unit
    })

    //update statuses statistics
    if (currentState.areaStatusSettings) {
      currentState.areaStatusSettings.areaStatuses.forEach(status => statistics.set(status.id, 0));
      updatedUnits.map(unit => {
        unit.rooms.map(room => {
          room.areas.forEach(area => statistics.set(area.status.id, statistics.get(area.status.id) + 1))
        })
      })
    }

    return {
      ...state,
      currentFloor: {
        ...state.currentFloor,
        units: updatedUnits
      },
      statistics: statistics
    };
  });

  readonly deleteStatusWithReplacement = this.updater((state: ProjectViewState, updatedStatusesInfo: {
    newStatus: ProjectCustomStatuses,
    deletedStatus: ProjectCustomStatuses
  }) => {
    const statistics = new Map(state.statistics);
    const updatedStatuses = [...state.areaStatusSettings.areaStatuses].filter(floor => floor.id !== updatedStatusesInfo.deletedStatus.id);

    const updatedUnits = state.currentFloor.units.map(unit => ({
      ...unit,
      rooms: unit.rooms.map(room => ({
        ...room,
        areas: room.areas.map(area => {
          if (area.status.id === updatedStatusesInfo.deletedStatus.id) {
            statistics.set(updatedStatusesInfo.newStatus.id, statistics.get(updatedStatusesInfo.newStatus.id) + 1);
            const updatedArea = {...area}
            updatedArea.status = {...updatedStatusesInfo.newStatus}
            return updatedArea;
          }
          return area;
        })
      }))
    }));

    return {
      ...state,
      currentFloor: {
        ...state.currentFloor,
        units: updatedUnits
      },
      statistics: statistics,
      areaStatusSettings: {
        ...state.areaStatusSettings,
        version: state.areaStatusSettings.version + 1,
        areaStatuses: updatedStatuses
      }
    };
  })


  readonly updateArea = this.updater((state: ProjectViewState, updatedArea: Area) => {
    const statistics = new Map(state.statistics);

    const updatedUnits = state.currentFloor.units.map(unit => ({
      ...unit,
      rooms: unit.rooms.map(room => ({
        ...room,
        areas: room.areas.map(area => {
          if (area.id === updatedArea.id) {
            if(area.status.id !== updatedArea.status.id) {
              statistics.set(area.status.id, statistics.get(area.status.id) - 1);
              statistics.set(updatedArea.status.id, statistics.get(updatedArea.status.id) + 1);
            }
            return updatedArea;
          }
          return area;
        })
      }))
    }));

    return {
      ...state,
      currentFloor: {
        ...state.currentFloor,
        units: updatedUnits
      },
      statistics: statistics
    };
  });


  readonly updateUnitName = this.updater((state: ProjectViewState, payload: { unitId: number, unitName: string }) => {
    const updatedUnits = state.currentFloor.units.map(unit => {
      if (unit.id === payload.unitId) {
        unit.name = payload.unitName
        return unit
      } else return unit
    });

    const updatedFloorUnitDuplicateMenuList = state.floorDuplicateMenuList.map(floor => ({
      ...floor,
      units: floor.units.map(unit => {
        if (unit.id === payload.unitId) {
          unit.name = payload.unitName
          return unit
        } else return unit
      })
    }))

    return {
      ...state,
      currentFloor: {
        ...state.currentFloor,
        units: updatedUnits
      },
      floorDuplicateMenuList: updatedFloorUnitDuplicateMenuList,
    };
  })

  readonly deleteUnit = this.updater((state: ProjectViewState, unitId: number) => {
    const updatedStatistics = new Map(state.statistics);

    let targetUnit = state.currentFloor.units.find(unit => unit.id === unitId);

    targetUnit.rooms.forEach(room => {
      room.areas.forEach(area => {
        updatedStatistics.set(area.status.id, updatedStatistics.get(area.status.id) - 1);
      })
    })

    let ind = state.currentFloor.units.findIndex(unit => unit.id === unitId);

    const updatedUnits = state.currentFloor.units.filter(unit => unit.id !== unitId);
    updatedUnits.forEach(unit => {
      if (updatedUnits.indexOf(unit) >= ind) {
        unit.version+=1;
      }
    });

    const updatedFloorUnitDuplicateMenuList = state.floorDuplicateMenuList.map(floor => ({
      ...floor,
      units: floor.units.filter(unit => unit.id !== unitId)
    }))

    return {
      ...state,
      currentFloor: {
        ...state.currentFloor,
        units: updatedUnits
      },
      floorDuplicateMenuList: updatedFloorUnitDuplicateMenuList,
      statistics: updatedStatistics
    };
  })

  readonly deleteTemplate = this.updater((state: ProjectViewState, templateId: number) => {
    const updatedTemplates = [...state.roomTemplatesShortList].filter(template => template.id !== templateId);

    return {
      ...state,
      roomTemplatesShortList: updatedTemplates
    }
  })

  readonly deleteFloor = this.updater((state: ProjectViewState, deletedFloor: Floor) => {
    let ind = state.floorsShortList.findIndex(floor => floor.id === deletedFloor.id);

    const updatedFloors = [...state.floorsShortList].filter(floor => floor.id !== deletedFloor.id);
    updatedFloors.forEach(floor => {
      if (updatedFloors.indexOf(floor) >= ind) floor.version+=1;
    });

    const updatedFloorUnitDuplicateMenuList = state.floorDuplicateMenuList.filter(floor => floor.name !== deletedFloor.name)

    return {
      ...state,
      floorsShortList: updatedFloors,
      floorDuplicateMenuList: updatedFloorUnitDuplicateMenuList
    }
  })


  readonly addUnit = this.updater((state: ProjectViewState, unit: Unit) => {
    const currentState = this.get();
    const statistics: Map<number, number> = new Map<number, number>();


    const units = state.currentFloor.units;
    units.push(unit);

    //update statuses statistics
    if (currentState.areaStatusSettings) {
      currentState.areaStatusSettings.areaStatuses.forEach(status => statistics.set(status.id, 0));
      units.map(u => {
        u.rooms.map(room => {
          room.areas.forEach(area => statistics.set(area.status.id, statistics.get(area.status.id) + 1))
        })
      })
    }

    return {
      ...state,
      currentFloor: {
        ...state.currentFloor,
        units: units
      },
      statistics: statistics
    };
  })

  readonly addFloor = this.updater((state: ProjectViewState, floor: Floor) => {
    const floors = [...state.floorsShortList];
    floors.push(floor);

    return {
      ...state,
      floorsShortList: floors
    };
  })

  readonly changeRoomOrder = this.updater((state: ProjectViewState, {unitId, newOrder, oldOrder}: {
    unitId: number,
    newOrder: number,
    oldOrder: number,
  }) => {
    if (oldOrder === newOrder) return state;
    let units = state.currentFloor.units;
    let unit = units.find(unit => unit.id === unitId);
    unit.rooms = moveItemInArrayCopyAndUpdateVersion(newOrder, oldOrder, unit.rooms);

    return {
      ...state,
      currentFloor: {
        ...state.currentFloor,
        units: units
      }
    };
  })

  readonly changeUnitOrder = this.updater((state: ProjectViewState, {newOrder, oldOrder}: {
    newOrder: number,
    oldOrder: number,
  }) => {

    if (oldOrder === newOrder) return state;

    return {
      ...state,
      currentFloor: {
        ...state.currentFloor,
        units: moveItemInArrayCopyAndUpdateVersion(newOrder, oldOrder, state.currentFloor.units)
      }
    };
  })

  readonly changeFloorOrder = this.updater((state: ProjectViewState, {newOrder, oldOrder}: {
    newOrder: number,
    oldOrder: number
  }) => {
    if (oldOrder === newOrder) return state;

    return {
      ...state,
      floorsShortList: moveItemInArrayCopyAndUpdateVersion(newOrder, oldOrder, state.floorsShortList)
    };
  })

  readonly updateIsCdkDragDisabled = this.updater((state: ProjectViewState, isDisabled: boolean) => {
    return {
      ...state,
      isCdkDragDisabled: isDisabled
    };
  })

  readonly updatePopupOpened = this.updater((state: ProjectViewState, popupOpened: PopupOpened) => {
    return {
      ...state,
      popupOpened: popupOpened
    };
  })

  override ngOnDestroy() {
    super.ngOnDestroy();
    this.destroySubj$.next()
    this.destroySubj$.complete()
  }

  private patchLoadedFloor(floor: Floor, expectedSectionId?: number | null) {
    const currentState = this.get();
    const currentSectionId = currentState.currentSection?.id;
    const targetSectionId = expectedSectionId ?? currentSectionId;

    if (!this.isFloorInSection(floor, targetSectionId, currentState.currentSection?.name)) {
      return;
    }

    if (targetSectionId && currentSectionId && targetSectionId !== currentSectionId) {
      return;
    }

    const statistics: Map<number, number> = new Map<number, number>();

    if (currentState.areaStatusSettings) {
      currentState.areaStatusSettings.areaStatuses.forEach(status => statistics.set(status.id, 0));

      floor.units.forEach(unit => {
        unit.rooms.forEach(room => {
          room.areas.forEach(area => statistics.set(area.status.id, statistics.get(area.status.id) + 1))
        })
      })
    }

    this.patchState({currentFloor: floor, statistics: statistics, floorInLoading: null});
  }

  private isFloorInSection(floor: Floor | null | undefined, sectionId?: number | null, sectionName?: string | null): boolean {
    if (!floor || !sectionId) return !!floor;
    if (floor.sectionId) return Number(floor.sectionId) === Number(sectionId);
    if (floor.sectionName && sectionName) return floor.sectionName === sectionName;
    return true;
  }

}
